import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Package, Edit, UserPlus, RotateCcw, Wrench, Paperclip, Upload, Trash2,
  Building2, Calendar, DollarSign, FileText, History, TrendingDown,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, AppButton, StatusBadge, ConfirmModal, AppModal,
} from "../ui";
import {
  ASSET_STATUS_BADGE, ASSET_STATUS_LABEL, ATTACHMENT_KINDS,
  formatCurrency, formatDate,
} from "./assetConstants";
import AssignAssetModal from "./AssignAssetModal";
import "./assets.scss";

const AssetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [asset, setAsset] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [showMaint, setShowMaint] = useState(false);
  const [maintForm, setMaintForm] = useState({ type: "service", description: "", cost: 0, performedBy: "", performedAt: "" });
  const [attachKind, setAttachKind] = useState("invoice");
  const [removeAttId, setRemoveAttId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/assets/${id}`);
      setAsset(data.asset);
      setAssignments(data.assignments || []);
      setMaintenance(data.maintenance || []);
    } catch {
      setToast({ show: true, message: "Failed to load asset.", type: "error" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [id]);

  const handleReturn = async () => {
    try {
      await apiClient.post(`/assets/${id}/return`, { conditionOnReturn: "good" });
      setShowReturn(false);
      setToast({ show: true, message: "Asset returned.", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Return failed.", type: "error" });
    }
  };

  const handleLogMaintenance = async () => {
    try {
      await apiClient.post(`/assets/${id}/maintenance`, {
        ...maintForm,
        cost: Number(maintForm.cost) || 0,
        performedAt: maintForm.performedAt || null,
      });
      setShowMaint(false);
      setMaintForm({ type: "service", description: "", cost: 0, performedBy: "", performedAt: "" });
      setToast({ show: true, message: "Maintenance logged.", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to log.", type: "error" });
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", attachKind);
    try {
      await apiClient.post(`/assets/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setToast({ show: true, message: "Attachment uploaded.", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Upload failed.", type: "error" });
    }
    e.target.value = "";
  };

  const handleRemoveAttachment = async () => {
    if (!removeAttId) return;
    try {
      await apiClient.delete(`/assets/${id}/attachments/${removeAttId}`);
      setRemoveAttId(null);
      setToast({ show: true, message: "Attachment removed.", type: "success" });
      fetchData();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Remove failed.", type: "error" });
      setRemoveAttId(null);
    }
  };

  if (loading) return <div className="assets-page"><div className="assets-page__empty">Loading...</div></div>;
  if (!asset) return <div className="assets-page"><div className="assets-page__empty">Asset not found.</div></div>;

  const isAssigned = asset.status === "assigned";

  return (
    <div className="assets-page">
      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      <PageHeader
        backButton
        onBack={() => navigate("/assets")}
        icon={Package}
        title={`${asset.assetTag} — ${asset.name}`}
        description={[asset.brand, asset.model].filter(Boolean).join(" • ")}
        actions={
          <>
            <AppButton variant="secondary" onClick={() => navigate(`/assets/${id}/edit`)}>
              <Edit size={16} /> Edit
            </AppButton>
            {!isAssigned ? (
              <AppButton onClick={() => setShowAssign(true)}>
                <UserPlus size={16} /> Assign
              </AppButton>
            ) : (
              <AppButton variant="secondary" onClick={() => setShowReturn(true)}>
                <RotateCcw size={16} /> Return
              </AppButton>
            )}
            <AppButton variant="secondary" onClick={() => setShowMaint(true)}>
              <Wrench size={16} /> Log Maintenance
            </AppButton>
          </>
        }
      />

      <div className="asset-detail">
        {/* ─── Main column ───────────────────────────────────────── */}
        <div className="asset-detail__main">
          <section className="asset-detail__card">
            <h3 className="asset-detail__card-title"><FileText size={16} /> Overview</h3>
            <DetailRow label="Status">
              <StatusBadge status={ASSET_STATUS_BADGE[asset.status] || "active"}>
                {ASSET_STATUS_LABEL[asset.status] || asset.status}
              </StatusBadge>
            </DetailRow>
            <DetailRow label="Condition">{asset.condition}</DetailRow>
            <DetailRow label="Category">{asset.categoryId?.name || "—"}</DetailRow>
            <DetailRow label="Branch">{asset.branchId?.name || "—"}</DetailRow>
            <DetailRow label="Location">{asset.location || "—"}</DetailRow>
            <DetailRow label="Serial Number">{asset.serialNumber || "—"}</DetailRow>
            <DetailRow label="Description">{asset.description || "—"}</DetailRow>
          </section>

          <section className="asset-detail__card">
            <h3 className="asset-detail__card-title"><DollarSign size={16} /> Purchase & Depreciation</h3>
            <DetailRow label="Vendor">{asset.vendorId?.name || "—"}</DetailRow>
            <DetailRow label="Invoice #">{asset.invoiceNumber || "—"}</DetailRow>
            <DetailRow label="Purchased On">{formatDate(asset.purchaseDate)}</DetailRow>
            <DetailRow label="Cost">{formatCurrency(asset.purchaseCost, asset.currency)}</DetailRow>
            <DetailRow label="Tax">{formatCurrency(asset.taxAmount, asset.currency)}</DetailRow>
            <DetailRow label="Warranty Expiry">{formatDate(asset.warrantyExpiry)}</DetailRow>
            <DetailRow label="AMC Expiry">{formatDate(asset.amcExpiry)}</DetailRow>
            <DetailRow label="Depreciation Method">{asset.depreciationMethod}</DetailRow>
            <DetailRow label="Current Book Value">{formatCurrency(asset.currentBookValue, asset.currency)}</DetailRow>
          </section>

          <section className="asset-detail__card">
            <h3 className="asset-detail__card-title"><History size={16} /> Assignment History</h3>
            {assignments.length === 0 ? (
              <p className="asset-detail__row-value">No assignments yet.</p>
            ) : (
              <div className="asset-detail__timeline">
                {assignments.map(a => (
                  <div key={a._id} className="asset-detail__timeline-item">
                    <div><strong>{a.assigneeName || a.assigneeId}</strong> ({a.assigneeType})</div>
                    <div className="asset-detail__timeline-item-meta">
                      Issued {formatDate(a.assignedAt)} • Returned {a.returnedAt ? formatDate(a.returnedAt) : "—"}
                      {a.expectedReturnAt && <> • Due {formatDate(a.expectedReturnAt)}</>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="asset-detail__card">
            <h3 className="asset-detail__card-title"><Wrench size={16} /> Maintenance Log</h3>
            {maintenance.length === 0 ? (
              <p className="asset-detail__row-value">No maintenance records.</p>
            ) : (
              <div className="asset-detail__timeline">
                {maintenance.map(m => (
                  <div key={m._id} className="asset-detail__timeline-item">
                    <div><strong>{m.type}</strong> — {m.description || "(no notes)"}</div>
                    <div className="asset-detail__timeline-item-meta">
                      {formatDate(m.performedAt)} • {m.performedBy || "—"}
                      {m.cost ? ` • ${formatCurrency(m.cost)}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* ─── Side column ──────────────────────────────────────── */}
        <div className="asset-detail__side">
          {asset.currentAssignment?.assigneeId && (
            <section className="asset-detail__card">
              <h3 className="asset-detail__card-title"><UserPlus size={16} /> Currently Assigned</h3>
              <DetailRow label="Assignee">{asset.currentAssignment.assigneeName}</DetailRow>
              <DetailRow label="Type">{asset.currentAssignment.assigneeType}</DetailRow>
              <DetailRow label="Issued On">{formatDate(asset.currentAssignment.assignedAt)}</DetailRow>
              {asset.currentAssignment.expectedReturnAt && (
                <DetailRow label="Expected Return">{formatDate(asset.currentAssignment.expectedReturnAt)}</DetailRow>
              )}
            </section>
          )}

          <section className="asset-detail__card">
            <h3 className="asset-detail__card-title"><Paperclip size={16} /> Attachments</h3>
            <div className="asset-detail__attachments">
              {(asset.attachments || []).length === 0 && (
                <p className="asset-detail__row-value">No attachments.</p>
              )}
              {(asset.attachments || []).map(att => (
                <div key={att._id} className="asset-detail__attachment">
                  <a className="asset-detail__attachment-name" href={att.url || "#"} target="_blank" rel="noopener noreferrer">
                    <Paperclip size={14} />
                    <span>{att.fileName}</span>
                  </a>
                  <AppButton size="sm" variant="ghost" onClick={() => setRemoveAttId(att._id)} aria-label="Remove">
                    <Trash2 size={14} />
                  </AppButton>
                </div>
              ))}
            </div>

            <div className="asset-form__field asset-detail__attach-kind">
              <select
                className="asset-form__select"
                value={attachKind}
                onChange={e => setAttachKind(e.target.value)}
              >
                {ATTACHMENT_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={handleUpload}
            />
            <AppButton variant="secondary" onClick={() => fileInputRef.current?.click()}>
              <Upload size={14} /> Upload File
            </AppButton>
          </section>
        </div>
      </div>

      {/* Modals */}
      <AssignAssetModal
        show={showAssign}
        onHide={() => setShowAssign(false)}
        assetId={id}
        onAssigned={fetchData}
      />

      <ConfirmModal
        show={showReturn}
        title="Return Asset?"
        message={`Mark "${asset.name}" as returned and back in stock?`}
        confirmText="Return"
        variant="info"
        onConfirm={handleReturn}
        onHide={() => setShowReturn(false)}
      />

      <ConfirmModal
        show={!!removeAttId}
        title="Remove Attachment?"
        message="This will permanently delete the file from storage."
        confirmText="Remove"
        variant="danger"
        onConfirm={handleRemoveAttachment}
        onHide={() => setRemoveAttId(null)}
      />

      <AppModal show={showMaint} onHide={() => setShowMaint(false)} title="Log Maintenance" size="md">
        <div>
          <div className="assets-modal__field">
            <label className="assets-modal__label">Type</label>
            <select className="assets-modal__select" value={maintForm.type} onChange={e => setMaintForm({ ...maintForm, type: e.target.value })}>
              <option value="service">Service</option>
              <option value="repair">Repair</option>
              <option value="upgrade">Upgrade</option>
              <option value="inspection">Inspection</option>
              <option value="cleaning">Cleaning</option>
            </select>
          </div>
          <div className="assets-modal__field">
            <label className="assets-modal__label">Description</label>
            <textarea className="assets-modal__textarea" value={maintForm.description} onChange={e => setMaintForm({ ...maintForm, description: e.target.value })} rows={3} />
          </div>
          <div className="assets-modal__field">
            <label className="assets-modal__label">Performed By</label>
            <input className="assets-modal__input" value={maintForm.performedBy} onChange={e => setMaintForm({ ...maintForm, performedBy: e.target.value })} />
          </div>
          <div className="assets-modal__field">
            <label className="assets-modal__label">Date</label>
            <input type="date" className="assets-modal__input" value={maintForm.performedAt} onChange={e => setMaintForm({ ...maintForm, performedAt: e.target.value })} />
          </div>
          <div className="assets-modal__field">
            <label className="assets-modal__label">Cost</label>
            <input type="number" min="0" step="0.01" className="assets-modal__input" value={maintForm.cost} onChange={e => setMaintForm({ ...maintForm, cost: e.target.value })} />
          </div>
          <div className="asset-form__actions">
            <AppButton variant="secondary" onClick={() => setShowMaint(false)}>Cancel</AppButton>
            <AppButton onClick={handleLogMaintenance}><Wrench size={14} /> Log</AppButton>
          </div>
        </div>
      </AppModal>
    </div>
  );
};

const DetailRow = ({ label, children }) => (
  <div className="asset-detail__row">
    <div className="asset-detail__row-label">{label}</div>
    <div className="asset-detail__row-value">{children}</div>
  </div>
);

export default AssetDetailPage;
